import React, { Fragment } from 'react';
import { XTable, useCurrency, XBox, XButton, UseAsync, UseCheckboxes } from '@orderx/lab/ui';
import { UiInput } from '@open-stack/ui';
import { XAlert } from '../alert/XAlert';

type Table = {
    header: {
        rows: RowHeader[];
    };
    body: {
        rows: RowBody[];
    };
    pagination: {
        cursorStart: number;
        cursorEnd: number;
        itemCount: number;
        pageSize: number;
        pageCount: number;
        options: {
            label: string;
            value: number;
        }[];
    };
    buttonPreviousParams: any;
    buttonNextParams: any;
};

type RowHeader = {
    key?: any;
    cells: {
        value: any;
        key?: any;
        align?: string;
        type?: 'currency' | string;
    }[];
};

type RowBody = RowHeader & {
    item: any;
};

export interface XTableApiProps {
    query: UseAsync;
    checkboxes?: UseCheckboxes<any>;
    mapCellByType?: {
        [key: string]: (T) => void;
    };
}

export function XTableApi({ query, checkboxes, mapCellByType }: XTableApiProps) {
    const currency = useCurrency();
    const table: Table = query.result?.table;

    function getCell(cell, options?: { isHeader?: boolean }) {
        const mapCellValue: any = mapCellByType?.[cell.type];
        const sharedProps = { align: cell.align };
        const cellType = cell.type?.toUpperCase();

        if (['CURRENCY', 'ACTION'].includes(cellType)) {
            sharedProps.align = sharedProps.align || 'right';
        }

        const renderCell = (children, props?: any) => (
            <td {...sharedProps} {...props} key={cell.key}>
                {children}
            </td>
        );

        if (options?.isHeader) {
            if (cellType === 'CHECKBOX') {
                return <Fragment key={cell.key}>{checkboxes?.table.getHeaderCell()}</Fragment>;
            }

            return renderCell(cell.value);
        }

        if (mapCellValue) {
            return renderCell(mapCellValue(cell.value));
        }

        if (cellType === 'CHECKBOX') {
            return <Fragment key={cell.key}>{checkboxes?.table.getCell(cell)}</Fragment>;
        }

        if (cellType === 'CURRENCY') {
            return renderCell(currency.format(cell.value));
        }

        return renderCell(cell.value);
    }

    if (!table) {
        return null;
    }

    const { buttonPreviousParams, buttonNextParams, pagination } = table;

    return (
        <>
            {table.body.rows.length > 0 ? (
                <XTable>
                    {table.header && (
                        <thead>
                            {table.header.rows.map((row: any) => (
                                <tr key={row.key}>{row.cells.map((cell) => getCell(cell, { isHeader: true }))}</tr>
                            ))}
                        </thead>
                    )}

                    <tbody>
                        {table.body.rows.map((row: any) => (
                            <tr key={row.key}>{row.cells.map((cell) => getCell(cell))}</tr>
                        ))}
                    </tbody>
                </XTable>
            ) : (
                <XAlert>No data</XAlert>
            )}

            {(buttonPreviousParams || buttonNextParams) && (
                <>
                    <XBox flex flexChildren>
                        <div>
                            <XButton
                                onClick={() => query.run(buttonPreviousParams)}
                                disabled={!buttonPreviousParams}
                                variant={'outlined'}
                                icon={'keyboard_arrow_left'}
                            />
                        </div>

                        <XBox gap={1} flex justify={'center'}>
                            <XButton variant={'outlined'}>
                                {table.pagination.cursorStart + 1}-{table.pagination.cursorEnd}
                                {' of '}
                                {table.pagination.itemCount}
                            </XButton>

                            <UiInput
                                value={pagination.pageSize}
                                onChangeValue={(pageSize) => query.run({ pageSize })}
                                options={pagination.options}
                                style={{ width: 0 }}
                            />
                        </XBox>

                        <XBox flex justify={'end'}>
                            <XButton
                                onClick={() => query.run(buttonNextParams)}
                                disabled={!buttonNextParams}
                                variant={'outlined'}
                                icon={'keyboard_arrow_right'}
                            />
                        </XBox>
                    </XBox>
                </>
            )}
        </>
    );
}
